import React, { useEffect, useState, useMemo } from "react";
import { Modal, Row, Col, notification, Button, Skeleton, Divider } from "antd";
import axios from "axios";
import moment from "moment";

const confirm = Modal.confirm;

const openNotificationWithIcon = (type, message, description) => {
    notification[type]({ message, description });
};

const parseJson = (value, fallback = {}) => {
    if (!value) return fallback;
    if (typeof value === "object" && !Array.isArray(value)) return value;
    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : fallback;
    } catch {
        return fallback;
    }
};

const parseArray = (value, fallback = []) => {
    if (!value) return fallback;
    if (Array.isArray(value)) return value;
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : fallback;
    } catch {
        return fallback;
    }
};

const getErrorMessage = error => {
    const data = error?.response?.data;
    if (!data) return "Ocorreu um erro inesperado.";
    if (typeof data === "string") return data;
    if (data.title && data.errors) {
        const validationMessages = Object.values(data.errors).flat().join(" | ");
        return `${data.title}${validationMessages ? `: ${validationMessages}` : ""}`;
    }
    if (data.title) return data.title;
    return "Ocorreu um erro inesperado.";
};

// ── Campos das secções de lista ──────────────────────────────────────────────

const CAMPOS_DESEMPENHO = [
    { key: "disciplina", label: "Disciplina" },
    { key: "desenvolvimento", label: "Desenvolvimento" },
    { key: "avancos", label: "Avanços" },
    { key: "dificuldades", label: "Dificuldades" },
    { key: "observacoes", label: "Observações" }
];

const CAMPOS_INTERVENCOES = [
    { key: "atividade", label: "Atividade" },
    { key: "frequencia", label: "Frequência" },
    { key: "resultados", label: "Resultados" },
    { key: "sugestoes", label: "Sugestões" }
];

// ── Modal ────────────────────────────────────────────────────────────────────

const ModalRelatorioIrandubaFundamental = ({ id, visible, onCancel, onDelete }) => {
    const [relatorio, setRelatorio] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const apresentacao = useMemo(() => parseJson(relatorio?.apresentacao), [relatorio?.apresentacao]);
    const desempenho = useMemo(
        () => parseArray(relatorio?.desempenho_academico_por_area_curricular),
        [relatorio?.desempenho_academico_por_area_curricular]
    );
    const intervencoes = useMemo(
        () => parseArray(relatorio?.intervencoes_na_sala_recursos_multifuncionais),
        [relatorio?.intervencoes_na_sala_recursos_multifuncionais]
    );

    const carregarDetalhe = id => {
        setIsLoading(true);
        axios({ method: "get", url: "/api/relatorio-individual/carregar-relatorio-individual-fundamental", params: { id } })
            .then(response => setRelatorio(response.data || {}))
            .catch(error => openNotificationWithIcon("error", "Erro", getErrorMessage(error)))
            .finally(() => setIsLoading(false));
    };

    const validarExcluir = () => {
        confirm({
            title: "Pretende remover este relatório?",
            okText: "Confirmar",
            cancelText: "Cancelar",
            okType: "danger",
            onOk: () => excluir()
        });
    };

    const excluir = () => {
        axios({ method: "put", url: "/api/relatorio-individual/excluir-relatorio-individual-fundamental", params: { id } })
            .then(() => {
                openNotificationWithIcon("success", "Relatório removido", "O relatório foi removido com sucesso.");
                onDelete();
            })
            .catch(error => openNotificationWithIcon("error", "Erro", getErrorMessage(error)));
    };

    useEffect(() => {
        if (id && visible) carregarDetalhe(id);
    }, [id]);

    // ── Render helpers ───────────────────────────────────────────────────────

    const renderField = (label, value, span = 12) => (
        <Col span={span} style={{ marginBottom: 12 }}>
            <span className="field-label">{label}</span>
            <Skeleton loading={isLoading} paragraph={false} active className="field-skeleton">
                <p className="field-value" style={{ fontWeight: "bold", marginBottom: 0 }}>
                    {value || "-"}
                </p>
            </Skeleton>
        </Col>
    );

    const renderFieldWide = (label, value) => (
        <Col span={24} style={{ marginBottom: 12 }}>
            {label && <span className="field-label">{label}</span>}
            <Skeleton loading={isLoading} title={false} active className="field-skeleton">
                <p className="field-value--wide" style={{ fontWeight: "bold", marginBottom: 0 }}>
                    {value || "-"}
                </p>
            </Skeleton>
        </Col>
    );

    const renderCardList = (lista, campos) => (
        <Skeleton loading={isLoading} active paragraph={{ rows: 4 }}>
            {lista?.length > 0 ? (
                lista.map((item, index) => (
                    <div key={index} style={{ border: "1px solid #e8e8e8", borderRadius: 4, padding: 12, marginBottom: 12 }}>
                        {campos.map(({ key, label }) => (
                            <p key={key} className="field-value--wide" style={{ marginBottom: 6 }}>
                                <span className="field-label" style={{ display: "inline" }}>
                                    {label}:{" "}
                                </span>
                                <span style={{ fontWeight: "bold" }}>{item[key] || "-"}</span>
                            </p>
                        ))}
                    </div>
                ))
            ) : (
                <p className="field-value--wide" style={{ fontWeight: "bold" }}>
                    -
                </p>
            )}
        </Skeleton>
    );

    return (
        <Modal
            title={
                <div>
                    <div style={{ fontWeight: "bold", fontSize: 16 }}>Relatório Individual Fundamental</div>
                    <div style={{ fontSize: 12, color: "#888" }}>Visualização do relatório individual</div>
                </div>
            }
            className="modal-detalhe-info"
            visible={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancelar" onClick={onCancel}>
                    Fechar
                </Button>,
                <Button key="eliminar" className="modal-detalhe-info-delete-btn" type="danger" onClick={validarExcluir}>
                    Excluir Relatório
                </Button>
            ]}
            centered
            width={900}
            bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
        >
            <div className="modal-detalhe-info__body">
                <div className="modal-detalhe-info__section">
                    {/* Apresentação */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        Apresentação
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {renderField("Ano Letivo", apresentacao.ano_letivo)}
                        {renderField("Bimestre", apresentacao.bimestre)}
                        {renderField("Aluno(a)", apresentacao.aluno, 24)}
                        {renderField(
                            "Data de Nascimento",
                            apresentacao.data_nascimento ? moment(apresentacao.data_nascimento).format("DD/MM/YYYY") : null
                        )}
                        {renderField("Série / Turma", apresentacao.serie_turma)}
                        {renderField("Professor(a) SRM", apresentacao.professor_srm)}
                        {renderField("Pedagogo(a) / Coordenador(a)", apresentacao.pedagogo_coordenador)}
                        {renderField("Responsáveis Legais", apresentacao.responsaveis_legais, 24)}
                    </Row>

                    {/* 1. Desempenho Acadêmico */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        1. Desempenho Acadêmico por Área Curricular
                    </Divider>
                    {renderCardList(desempenho, CAMPOS_DESEMPENHO)}

                    {/* 2. Intervenções */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        2. Intervenções na Sala de Recursos Multifuncionais
                    </Divider>
                    {renderCardList(intervencoes, CAMPOS_INTERVENCOES)}

                    {/* 3. Considerações Gerais */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        3. Considerações Gerais
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {renderFieldWide(null, relatorio?.consideracoes_gerais)}
                    </Row>
                </div>
            </div>
        </Modal>
    );
};

export default ModalRelatorioIrandubaFundamental;
